import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { KanbanBoard } from '../components/KanbanBoard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, X, Calendar, DollarSign, Building2, LayoutGrid, List, ChevronRight, FolderOpen, Users } from 'lucide-react';

const PROJECT_COLUMNS = [
  { id: 'not_started', title: 'Not Started', color: 'gray' },
  { id: 'in_progress', title: 'In Progress', color: 'blue' },
  { id: 'ongoing', title: 'Ongoing', color: 'purple' },
  { id: 'on_hold', title: 'On Hold', color: 'yellow' },
  { id: 'completed', title: 'Completed', color: 'green' },
  { id: 'cancelled', title: 'Cancelled', color: 'red' },
];

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-red-100 text-red-700',
};

const STATUS_COLORS = {
  not_started: 'bg-gray-500 text-white',
  in_progress: 'bg-[#014DB7] text-white',
  ongoing: 'bg-purple-500 text-white',
  on_hold: 'bg-yellow-500 text-white',
  completed: 'bg-[#84CC16] text-white',
  cancelled: 'bg-red-500 text-white',
};

const PROJECT_TYPES = [
  'Web Design', 'DME Setup', 'CRM Implementation', 'Consulting', 'Training', 
  'System Integration', 'Custom Development', 'Support Contract', 'Other'
];

const ProjectCard = ({ project, users }) => {
  const navigate = useNavigate();
  const assignedUsers = users.filter(u => project.assigned_to?.includes(u.id));
  
  const formatBudget = (budget) => {
    if (!budget) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(budget);
  };
  
  return (
    <div 
      className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm hover:shadow-lg hover:border-[#014DB7]/40 transition-all duration-200 group cursor-pointer"
      onClick={() => navigate(`/crm/projects/${project.id}`)}
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0 pr-4">
          <h4 className="font-semibold text-[#0A1628] group-hover:text-[#014DB7] transition-colors truncate text-sm">{project.name}</h4>
          {project.client_name && (
            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{project.client_name}</span>
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[project.priority] || PRIORITY_COLORS.medium}`}>
          {project.priority}
        </span>
        <div className="flex items-center gap-2">
          {project.budget && (
            <span className="text-xs text-green-600 font-medium flex items-center gap-0.5">
              <DollarSign className="w-3 h-3" /> {formatBudget(project.budget)}
            </span>
          )}
        </div>
      </div>
      
      {assignedUsers.length > 0 && (
        <div className="mt-2 flex -space-x-1.5">
          {assignedUsers.slice(0, 3).map(user => (
            <div key={user.id} className="w-5 h-5 bg-[#014DB7] rounded-full flex items-center justify-center border-2 border-white" title={`${user.first_name} ${user.last_name}`}>
              <span className="text-white text-[8px] font-medium">{user.first_name?.[0]}{user.last_name?.[0]}</span>
            </div>
          ))}
          {assignedUsers.length > 3 && (
            <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center border-2 border-white">
              <span className="text-gray-600 text-[8px]">+{assignedUsers.length - 3}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProjectListRow = ({ project, users, onStatusChange }) => {
  const assignedUsers = users.filter(u => project.assigned_to?.includes(u.id));
  const navigate = useNavigate();
  
  const formatBudget = (budget) => {
    if (!budget) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(budget);
  };
  
  return (
    <div 
      className="flex items-center gap-4 p-4 bg-white border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors group"
      onClick={() => navigate(`/crm/projects/${project.id}`)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#014DB7]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <FolderOpen className="w-5 h-5 text-[#014DB7]" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-[#0A1628] group-hover:text-[#014DB7] transition-colors truncate">
              {project.name}
            </h4>
            {project.client_name && (
              <p className="text-sm text-gray-500 truncate">{project.client_name}</p>
            )}
          </div>
        </div>
      </div>
      
      <div className="w-28 hidden lg:block">
        <span className="text-sm text-gray-600">{project.project_type || '-'}</span>
      </div>
      
      <div className="w-28" onClick={(e) => e.stopPropagation()}>
        <Select value={project.status} onValueChange={(v) => onStatusChange(project.id, v)}>
          <SelectTrigger className="h-8 text-xs rounded-lg border-0 bg-transparent hover:bg-gray-100">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}>
              {PROJECT_COLUMNS.find(c => c.id === project.status)?.title}
            </span>
          </SelectTrigger>
          <SelectContent>
            {PROJECT_COLUMNS.map(col => (
              <SelectItem key={col.id} value={col.id}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[col.id]?.replace('text-white', '')}`} />
                  {col.title}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="w-24 hidden md:block text-right">
        <span className="text-sm font-medium text-green-600">{formatBudget(project.budget)}</span>
      </div>
      
      <div className="w-20 hidden sm:block">
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${PRIORITY_COLORS[project.priority]}`}>
          {project.priority}
        </span>
      </div>
      
      <div className="w-16">
        {assignedUsers.length > 0 ? (
          <div className="flex -space-x-2">
            {assignedUsers.slice(0, 2).map(user => (
              <div key={user.id} className="w-7 h-7 bg-[#84CC16] rounded-full flex items-center justify-center border-2 border-white" title={`${user.first_name} ${user.last_name}`}>
                <span className="text-white text-[10px] font-medium">{user.first_name?.[0]}{user.last_name?.[0]}</span>
              </div>
            ))}
            {assignedUsers.length > 2 && (
              <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center border-2 border-white">
                <span className="text-gray-600 text-[10px]">+{assignedUsers.length - 2}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center">
            <Users className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>
      
      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#014DB7] transition-colors" />
    </div>
  );
};

const CreateProjectModal = ({ isOpen, onClose, project, onSave, users }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', status: 'not_started', priority: 'medium',
    start_date: '', due_date: '', assigned_to: [], project_type: '',
    client_name: '', company: '', client_email: '', client_phone: '',
    budget: '', website_url: '', notes: '',
  });

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        status: project.status || 'not_started',
        priority: project.priority || 'medium',
        start_date: project.start_date || '',
        due_date: project.due_date || '',
        assigned_to: project.assigned_to || [],
        project_type: project.project_type || '',
        client_name: project.client_name || '',
        company: project.company || '',
        client_email: project.client_email || '',
        client_phone: project.client_phone || '',
        budget: project.budget || '',
        website_url: project.website_url || '',
        notes: project.notes || '',
      });
    } else {
      setFormData({
        name: '', description: '', status: 'not_started', priority: 'medium',
        start_date: '', due_date: '', assigned_to: [], project_type: '',
        client_name: '', company: '', client_email: '', client_phone: '',
        budget: '', website_url: '', notes: '',
      });
    }
  }, [project, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      budget: formData.budget ? parseFloat(formData.budget) : null,
    };
    onSave(submitData);
  };

  const toggleAssignee = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-[#0A1628]">
            {project ? 'Edit Project' : 'Create New Project'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Project Info Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-[#014DB7]" />
              Project Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-xl"
                  placeholder="Enter project name"
                />
              </div>
              <div className="space-y-2">
                <Label>Project Type</Label>
                <Select value={formData.project_type} onValueChange={(v) => setFormData({ ...formData, project_type: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 mt-4">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="rounded-xl"
                placeholder="Describe the project scope..."
              />
            </div>
          </div>

          {/* Client Info Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#84CC16]" />
              Client Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Name</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="rounded-xl"
                  placeholder="Client name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="rounded-xl"
                  placeholder="Company name"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="rounded-xl"
                  placeholder="client@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Phone</Label>
                <Input
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  className="rounded-xl"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Dates, Budget & Status Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#014DB7]" />
              Timeline & Budget
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="rounded-xl"
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_COLUMNS.map(col => (
                      <SelectItem key={col.id} value={col.id}>{col.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Team Assignment */}
          <div className="space-y-2">
            <Label>Assign Team Members</Label>
            <div className="flex flex-wrap gap-2">
              {users.map(user => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => toggleAssignee(user.id)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    formData.assigned_to.includes(user.id)
                      ? 'bg-[#014DB7] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {user.first_name} {user.last_name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
              {project ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ProjectsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'

  const fetchData = async () => {
    try {
      const [projectsRes, usersRes] = await Promise.all([
        api.get('/api/projects'),
        api.get('/api/users'),
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleItemMove = async (itemId, newStatus) => {
    setProjects(prev => prev.map(project => 
      project.id === itemId ? { ...project, status: newStatus } : project
    ));

    try {
      await api.put(`/api/projects/${itemId}`, { status: newStatus });
    } catch (error) {
      toast.error('Failed to update project');
      fetchData();
    }
  };

  const handleSaveProject = async (data) => {
    try {
      if (selectedProject) {
        await api.put(`/api/projects/${selectedProject.id}`, data);
        toast.success('Project updated');
      } else {
        await api.post('/api/projects', data);
        toast.success('Project created');
      }
      setModalOpen(false);
      setSelectedProject(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Projects</h1>
          <p className="text-gray-500">Manage your project pipeline</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'kanban' 
                  ? 'bg-white text-[#014DB7] shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="kanban-view-btn"
            >
              <LayoutGrid className="w-4 h-4" />
              Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'list' 
                  ? 'bg-white text-[#014DB7] shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4" />
              List
            </button>
          </div>
          
          <Button 
            onClick={() => { setSelectedProject(null); setModalOpen(true); }} 
            className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl"
            data-testid="create-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Project
          </Button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <KanbanBoard
          columns={PROJECT_COLUMNS}
          items={projects}
          onItemMove={handleItemMove}
          renderItem={(project) => (
            <ProjectCard project={project} users={users} />
          )}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {/* List Header */}
          <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="flex-1">Project</div>
            <div className="w-28 hidden lg:block">Type</div>
            <div className="w-28">Status</div>
            <div className="w-24 hidden md:block text-right">Budget</div>
            <div className="w-20 hidden sm:block">Priority</div>
            <div className="w-16">Team</div>
            <div className="w-5"></div>
          </div>
          
          {/* List Rows */}
          <div className="divide-y divide-gray-100">
            {projects.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No projects found</div>
            ) : (
              projects.map(project => (
                <ProjectListRow 
                  key={project.id} 
                  project={project} 
                  users={users}
                  onStatusChange={handleItemMove}
                />
              ))
            )}
          </div>
        </div>
      )}

      <CreateProjectModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedProject(null); }}
        project={selectedProject}
        onSave={handleSaveProject}
        users={users}
      />
    </div>
  );
};

export default ProjectsPage;
